/**
 * Analytics Sidebar Component
 * Navigation sidebar for analytics dashboard
 */

import { Link, useLocation } from 'react-router-dom';
import { usePermissions } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import {
  LayoutDashboard, TrendingUp, MapPin,
  BarChart3, UserCheck, MessageSquare,
  GraduationCap, FileText,
  Plug, Webhook, Settings, X
} from 'lucide-react';

// `permission` is what the item's ROUTE requires, so the nav can never offer a
// page that redirects to /access-denied. Every analytics page reads through
// analytics:read (the read dependency in analytics, qa, sentiment, compliance
// and location services is uniformly that code), so they match today. The
// narrower analytics:* codes gate write controls inside the pages, not routes —
// putting them here would hide working pages from a moderator.
const NAV_PERMISSION = StandardPermission.Analytics.READ;

export const SECTIONS = [
  {
    label: 'OVERVIEW',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', permission: NAV_PERMISSION },
    ],
  },
  {
    label: 'INTELLIGENCE',
    items: [
      { icon: MessageSquare, label: 'Conversations', path: '/conversations', permission: NAV_PERMISSION },
      { icon: TrendingUp, label: 'Sentiment', path: '/sentiment', permission: NAV_PERMISSION },
      { icon: MapPin, label: 'Locations', path: '/locations', permission: NAV_PERMISSION },
    ],
  },
  {
    label: 'DASHBOARDS',
    items: [
      { icon: BarChart3, label: 'Executive', path: '/executive', permission: NAV_PERMISSION },
      { icon: UserCheck, label: 'Agent View', path: '/agent', permission: NAV_PERMISSION },
    ],
  },
  {
    label: 'QUALITY',
    items: [
      { icon: GraduationCap, label: 'Coaching', path: '/coaching', permission: NAV_PERMISSION },
      { icon: FileText, label: 'Reports', path: '/reports', permission: NAV_PERMISSION },
    ],
  },
  {
    label: 'CONFIGURATION',
    items: [
      { icon: Plug, label: 'Connectors', path: '/connectors', permission: NAV_PERMISSION },
      { icon: Webhook, label: 'Webhooks', path: '/webhooks', permission: NAV_PERMISSION, comingSoon: true },
      { icon: Settings, label: 'Settings', path: '/settings', permission: NAV_PERMISSION },
    ],
  },
];

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { hasPermission } = usePermissions();

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleClick = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-[150px] bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:transform-none ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-3 border-b border-gray-200">
          <h2 className="text-sm font-bold text-gray-900">Analytics</h2>
          <button
            onClick={onClose}
            className="lg:hidden text-gray-500 hover:text-gray-700"
            aria-label="Close navigation menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="p-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 64px)' }}>
          {SECTIONS.map((section) => {
            // `!item.permission ||` is load-bearing: an item declared without the
            // field must stay visible, and hasPermission(undefined) throws on
            // .split(). A system_admin short-circuits on the '*' wildcard and
            // would never surface that, so it only breaks for everyone else.
            const items = section.items.filter(
              (item) => !item.permission || hasPermission(item.permission)
            );
            if (items.length === 0) return null; // no orphaned section header

            return (
            <div key={section.label} className="mb-3">
              <p className="px-2 py-1 text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                {section.label}
              </p>
              <ul className="space-y-0.5">
                {items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);

                  if (item.comingSoon) {
                    return (
                      <li key={item.path}>
                        <span className="w-full flex items-center space-x-2 px-2 py-2 rounded-lg text-gray-400 cursor-not-allowed">
                          <Icon size={16} className="text-gray-300" />
                          <span className="text-xs">{item.label}</span>
                          <span className="text-[9px] px-1 py-0.5 rounded bg-gray-200 text-gray-500 leading-none">Soon</span>
                        </span>
                      </li>
                    );
                  }

                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={handleClick}
                        className={`w-full flex items-center space-x-2 px-2 py-2 rounded-lg transition-colors ${
                          active
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Icon size={16} className={active ? 'text-primary-600' : 'text-gray-500'} />
                        <span className="text-xs">{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
